import { z } from "zod";
import { HttpError } from "@nasam/kernel";

/**
 * Capacity: what NASAM sold this world. Every module given to a client
 * company uses one of that module's companies; every active device uses one
 * of the devices, shared by all client companies. Going past either is never
 * refused -- the write goes through, the extra is billed as an overage, and
 * the response's `usageInfo` says where each capacity now stands. So the
 * user is warned before saving, from the capacity already on screen, and
 * told afterwards if `usageInfo` shows an overage nobody saw coming.
 */

/** Agreed with the backend: a capacity of exactly this many is unlimited. */
export const UNLIMITED_CAPACITY = 99999;

export interface Capacity {
  readonly paidQuantity: number;
  readonly used: number;
  /** Negative once usage is past what was paid for. */
  readonly remaining: number;
}

export function isUnlimitedCapacity(capacity: Capacity): boolean {
  return capacity.paidQuantity === UNLIMITED_CAPACITY;
}

/** Where a capacity stands after a write (`usageInfo` on its response). */
export interface CapacityUsage {
  readonly quantity: number;
  readonly used: number;
  /** 0 once it is all used. */
  readonly remaining: number;
  /** Taken past `quantity`; 0 while within it. */
  readonly overage: number;
}

export interface ModuleCapacityUsage extends CapacityUsage {
  readonly moduleId: string;
}

/** One capacity a write takes past what was paid for -- a row of the
 *  over-capacity warning. */
export interface CapacityOverage {
  readonly kind: "module" | "devices";
  /** `null` for the devices capacity. */
  readonly moduleId: string | null;
  readonly paidQuantity: number;
  /** Usage with the write made. */
  readonly currentUsage: number;
  /** How far `currentUsage` is past `paidQuantity`. */
  readonly additionalQuantity: number;
}

/**
 * The overage one more unit would take a limited capacity to, or `null`
 * while it stays within it (or the capacity is unlimited).
 */
export function predictedOverage(
  capacity: Capacity,
  kind: CapacityOverage["kind"],
  moduleId: string | null,
): CapacityOverage | null {
  if (isUnlimitedCapacity(capacity)) return null;
  const currentUsage = capacity.used + 1;
  const additionalQuantity = currentUsage - capacity.paidQuantity;
  return additionalQuantity > 0
    ? {
        kind,
        moduleId,
        paidQuantity: capacity.paidQuantity,
        currentUsage,
        additionalQuantity,
      }
    : null;
}

/** A capacity a write left past what was paid for, or `null`. */
export function overageOfUsage(
  usage: CapacityUsage,
  kind: CapacityOverage["kind"],
  moduleId: string | null,
): CapacityOverage | null {
  return usage.overage > 0
    ? {
        kind,
        moduleId,
        paidQuantity: usage.quantity,
        currentUsage: usage.used,
        additionalQuantity: usage.overage,
      }
    : null;
}

const usageDtoSchema = z.object({
  quantity: z.number(),
  used: z.number(),
  remaining: z.number(),
  overage: z.number(),
});

/** `usageInfo` on a sale's response: every module the world holds. */
export const moduleUsageInfoDtoSchema = z
  .object({
    modules: z.array(usageDtoSchema.extend({ moduleId: z.string() })),
  })
  .nullish()
  .transform((info): readonly ModuleCapacityUsage[] =>
    (info?.modules ?? []).map((usage) => ({ ...usage })),
  );

/** `usageInfo` on a device write's response: the world's devices. */
export const deviceUsageInfoDtoSchema = z
  .object({ devices: usageDtoSchema })
  .nullish()
  .transform((info): CapacityUsage | null =>
    info === null || info === undefined ? null : { ...info.devices },
  );

/** No active devices subscription: devices can't be added or activated. */
export interface NoDeviceCapacityRefusal {
  readonly status: "no-device-capacity";
  readonly message: string;
}

const noDeviceCapacityDtoSchema = z.object({
  code: z.literal("NO_DEVICE_CAPACITY"),
  message: z
    .string()
    .nullish()
    .transform((value) => value ?? ""),
});

/**
 * The refusal a device write gets with no devices subscription, or
 * `undefined` for any other error. The `code` is matched, not the message
 * -- that one is translated.
 */
export function noDeviceCapacityOf(
  error: unknown,
): NoDeviceCapacityRefusal | undefined {
  if (!(error instanceof HttpError) || error.status !== 409) return undefined;
  const none = noDeviceCapacityDtoSchema.safeParse(error.data);
  return none.success
    ? { status: "no-device-capacity", message: none.data.message }
    : undefined;
}

/** The extra units across every overage. */
export function totalAdditionalQuantity(
  overages: readonly CapacityOverage[],
): number {
  return overages.reduce((sum, overage) => sum + overage.additionalQuantity, 0);
}
